import { liveQuery } from 'dexie';
import { KINDS, rowToWire } from './schema.js';
import { mergeRemote } from './merge.js';
import { maxHlc } from './hlc.js';
import { dbSummary, summariesEqual } from './integrity.js';
import { withLock, LOCK_BUSY } from './locks.js';
import { writeTx, enqueueAll } from '../db/mutate.js';
import { getMeta, setMeta, observeHlc } from '../db/meta.js';
import { recomputeCards, recomputeDecks } from '../scheduler/recompute.js';

const ZERO = { tx: 0, seq: 0 };

/**
 * Push local outbox -> server, pull server changes -> local, verify replicas match.
 *
 * Guarantees:
 * - A local change is removed from the outbox only after the server accepted it.
 * - Server merge is idempotent, so a push that times out after committing is
 *   safely re-sent.
 * - The pull cursor advances in the same IndexedDB transaction that applies the
 *   rows, so a crash can never skip or half-apply a page.
 */
export function createSyncEngine({
  db,
  transport,
  onState = () => {},
  onSynced,
  lockName = 'recall-sync',
  useLock = true,
  pushBatch = 200,
  pullPage = 500,
  verifyEveryMs = 10 * 60 * 1000,
  intervalMs = 60 * 1000,
  debounceMs = 1500,
}) {
  let stopped = true;
  let chain = Promise.resolve();
  let queued = null;
  let queuedOpts = {};
  let failures = 0;
  let retryTimer = null;
  let debounceTimer = null;
  let intervalTimer = null;
  let lastVerify = 0;
  let outboxSub = null;
  let lastOutboxCount = 0;
  const cleanups = [];

  const isOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false);

  let runAuth = null;

  /** True when this run may still change the local replica for this account. */
  async function canWrite(userId) {
    if (!userId) return true;
    return (await getMeta(db, 'ownerId')) === userId;
  }

  async function pushAll() {
    let total = 0;
    for (;;) {
      if (!(await canWrite(runAuth?.userId))) return total;
      const entries = await db.outbox.orderBy('seq').limit(pushBatch * 3).toArray();
      if (!entries.length) return total;
      const groups = new Map();
      for (const e of entries) {
        const key = `${e.kind}:${e.id}`;
        if (!groups.has(key)) groups.set(key, { kind: e.kind, id: e.id, keys: new Set(), seqs: [] });
        const g = groups.get(key);
        e.keys.forEach((k) => g.keys.add(k));
        g.seqs.push(e.seq);
        if (groups.size >= pushBatch) break;
      }
      const changes = [];
      const seqs = [];
      for (const g of groups.values()) {
        seqs.push(...g.seqs);
        const def = KINDS[g.kind];
        const row = def ? await db[def.table].get(g.id) : null;
        if (!row) continue;
        const { data, hlc } = rowToWire(row, [...g.keys]);
        if (Object.keys(hlc).length) changes.push({ kind: g.kind, id: g.id, data, hlc });
      }
      if (changes.length) await transport.push(changes, runAuth);
      // The upload used the token from the start of this run. If the person
      // signed out or switched accounts while it was in flight, leave the
      // outbox alone so the new account's queue is not deleted.
      if (!(await canWrite(runAuth?.userId))) return total;
      await db.outbox.bulkDelete(seqs);
      total += changes.length;
    }
  }

  async function pullAll() {
    const cards = new Set();
    const decks = new Set();
    let pulled = 0;
    for (;;) {
      if (!(await canWrite(runAuth?.userId))) break;
      const cursor = (await getMeta(db, 'cursor')) ?? ZERO;
      const rows = await transport.pull(cursor, pullPage, runAuth);
      if (!rows.length) break;
      let outcome = 'applied';
      await writeTx(db, async () => {
        if (!(await canWrite(runAuth?.userId))) {
          outcome = 'stop';
          return;
        }
        // Re-check inside the transaction: another tab may have applied this page.
        const now = (await getMeta(db, 'cursor')) ?? ZERO;
        if (now.tx !== cursor.tx || now.seq !== cursor.seq) {
          outcome = 'retry';
          return;
        }
        let highest = null;
        for (const r of rows) {
          const def = KINDS[r.kind];
          if (!def) continue;
          const table = db[def.table];
          const local = await table.get(r.id);
          const { row, changed } = mergeRemote(local, r.id, r);
          highest = maxHlc([highest, ...Object.values(r.hlc ?? {})]);
          if (!changed.length) continue;
          if (r.kind === 'card') {
            if (!local) {
              row.due = row.createdAt ?? Date.now();
              row.fsrs = null;
            }
            cards.add(r.id);
          } else if (r.kind === 'reviewLog') {
            if (row.cardId) cards.add(row.cardId);
          } else if (r.kind === 'deck' && changed.some((k) => k.startsWith('config.'))) {
            decks.add(r.id);
          }
          await table.put(row);
        }
        if (highest) await observeHlc(db, highest);
        const last = rows[rows.length - 1];
        await setMeta(db, 'cursor', { tx: last.tx, seq: last.seq });
      });
      if (outcome === 'stop') break;
      if (outcome === 'retry') continue;
      pulled += rows.length;
      if (rows.length < pullPage) break;
    }
    if (cards.size) await recomputeCards(db, [...cards]);
    if (decks.size) await recomputeDecks(db, [...decks]);
    return pulled;
  }

  async function verify() {
    const remote = await transport.summary(runAuth);
    const local = await dbSummary(db);
    lastVerify = Date.now();
    if (summariesEqual(local, remote)) return 'ok';
    // A concurrent write from another device can cause a transient mismatch.
    await pushAll();
    await pullAll();
    if (!(await canWrite(runAuth?.userId))) return 'aborted';
    if (summariesEqual(await dbSummary(db), await transport.summary(runAuth))) return 'ok';
    // Self-heal: re-send every local field and re-read everything from the server.
    if (!(await canWrite(runAuth?.userId))) return 'aborted';
    await enqueueAll(db);
    await setMeta(db, 'cursor', ZERO);
    await pushAll();
    await pullAll();
    if (!(await canWrite(runAuth?.userId))) return 'aborted';
    return summariesEqual(await dbSummary(db), await transport.summary(runAuth)) ? 'repaired' : 'mismatch';
  }

  async function syncOnce(opts) {
    runAuth = transport.auth ? await transport.auth() : null;
    if (transport.auth && !runAuth?.userId) throw new Error('Not signed in');
    if (!(await canWrite(runAuth?.userId))) return { pushed: 0, pulled: 0, aborted: true };
    if (opts.full) {
      await enqueueAll(db);
      await setMeta(db, 'cursor', ZERO);
    }
    const pushed = await pushAll();
    if (!(await canWrite(runAuth?.userId))) return { pushed, pulled: 0, aborted: true };
    const pulled = await pullAll();
    let integrity = null;
    const due = opts.verify || Date.now() - lastVerify > verifyEveryMs;
    if (due && (await db.outbox.count()) === 0) integrity = await verify();
    return { pushed, pulled, integrity };
  }

  function scheduleRetry() {
    clearTimeout(retryTimer);
    const base = Math.min(2 ** failures * 2000, 5 * 60 * 1000);
    const delay = Math.round(base * (0.75 + Math.random() * 0.5));
    retryTimer = setTimeout(() => request('retry'), delay);
    return Date.now() + delay;
  }

  async function execute(opts) {
    if (stopped) return null;
    if (!isOnline()) {
      onState({ status: 'offline' });
      return null;
    }
    onState({ status: 'syncing' });
    try {
      const run = () => syncOnce(opts);
      const res = useLock ? await withLock(lockName, run) : await run();
      if (res === LOCK_BUSY) {
        // Another tab is syncing; its changes reach us through IndexedDB.
        onState({ status: 'idle' });
        clearTimeout(retryTimer);
        retryTimer = setTimeout(() => request('lock-retry'), 4000);
        return null;
      }
      failures = 0;
      clearTimeout(retryTimer);
      const patch = { status: 'idle', lastSyncAt: Date.now(), lastError: null, retryAt: null };
      if (res.integrity) Object.assign(patch, { integrity: res.integrity, lastIntegrityAt: Date.now() });
      onState(patch);
      await onSynced?.(res);
      return res;
    } catch (err) {
      failures++;
      const retryAt = stopped ? null : scheduleRetry();
      onState({ status: isOnline() ? 'error' : 'offline', lastError: err?.message ?? String(err), retryAt });
      return { error: err };
    }
  }

  /** Coalescing scheduler: at most one run in flight and one queued. */
  function request(_reason, opts = {}) {
    queuedOpts = {
      full: queuedOpts.full || opts.full,
      verify: queuedOpts.verify || opts.verify,
    };
    if (queued) return queued;
    queued = chain = chain.then(async () => {
      const o = queuedOpts;
      queued = null;
      queuedOpts = {};
      return execute(o);
    });
    return queued;
  }

  function on(target, event, fn) {
    target?.addEventListener?.(event, fn);
    cleanups.push(() => target?.removeEventListener?.(event, fn));
  }

  return {
    start() {
      if (!stopped) return;
      stopped = false;
      if (typeof window !== 'undefined') {
        on(window, 'online', () => request('online'));
        on(window, 'offline', () => onState({ status: 'offline' }));
        on(window, 'focus', () => request('focus'));
        on(document, 'visibilitychange', () => {
          if (document.visibilityState === 'visible') request('visible');
        });
        // Safari restores pages from the back/forward cache without a reload.
        on(window, 'pageshow', (e) => e.persisted && request('pageshow'));
      }
      if (intervalMs) {
        intervalTimer = setInterval(() => {
          if (typeof document === 'undefined' || document.visibilityState !== 'hidden') request('interval');
        }, intervalMs);
      }
      outboxSub = liveQuery(() => db.outbox.count()).subscribe({
        next: (count) => {
          if (count > lastOutboxCount) {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => request('local'), debounceMs);
          }
          lastOutboxCount = count;
        },
        error: () => {},
      });
      request('start', { verify: true });
    },
    stop() {
      stopped = true;
      clearTimeout(retryTimer);
      clearTimeout(debounceTimer);
      clearInterval(intervalTimer);
      outboxSub?.unsubscribe();
      cleanups.splice(0).forEach((fn) => fn());
      // Let the in-flight run finish its current request before the account changes.
      return chain.catch(() => {});
    },
    /** Request a sync; resolves with the result of the run that served it. */
    syncNow: (opts) => request('manual', opts),
    request,
    // Exposed for tests.
    _syncOnce: (opts = {}) => syncOnce(opts),
  };
}
